import React, { useRef, useEffect } from 'react';

const VerificationCodeInput = ({ code, onChange, error, disabled }) => {
  const inputRefs = useRef([]);
  const CODE_LENGTH = 8;

  useEffect(() => {
    if (inputRefs?.current?.[0]) {
      inputRefs?.current?.[0]?.focus();
    }
  }, []);

  const handleChange = (index, value) => {
    if (disabled) return;

    // อนุญาตเฉพาะ a-z, A-Z, 0-9
    const newValue = value?.replace(/[^a-zA-Z0-9]/g, '')?.slice(-1);

    if (value?.length > 1) {
      // Paste หลายตัว
      const pastedCode = value?.replace(/[^a-zA-Z0-9]/g, '')?.slice(0, CODE_LENGTH);
      const newCode = (code || '').padEnd(CODE_LENGTH, ' ').split('');

      for (let i = 0; i < pastedCode?.length && index + i < CODE_LENGTH; i++) {
        newCode[index + i] = pastedCode?.[i];
      }

      onChange(newCode.join('').trim());

      const nextIndex = Math.min(index + pastedCode?.length, CODE_LENGTH - 1);
      inputRefs?.current?.[nextIndex]?.focus();
      return;
    }

    const newCode = (code || '').padEnd(CODE_LENGTH, ' ').split('');
    newCode[index] = newValue || ' ';
    onChange(newCode.join('').trim());

    if (newValue && index < CODE_LENGTH - 1) {
      inputRefs?.current?.[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (disabled) return;

    if (e?.key === 'Backspace') {
      e?.preventDefault();
      const newCode = (code || '').padEnd(CODE_LENGTH, ' ').split('');

      if (code?.[index] && code[index] !== ' ') {
        newCode[index] = ' ';
        onChange(newCode.join('').trim());
      } else if (index > 0) {
        newCode[index - 1] = ' ';
        onChange(newCode.join('').trim());
        inputRefs?.current?.[index - 1]?.focus();
      }
    } else if (e?.key === 'ArrowLeft' && index > 0) {
      inputRefs?.current?.[index - 1]?.focus();
    } else if (e?.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      inputRefs?.current?.[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pastedData = e?.clipboardData?.getData('text')?.replace(/[^a-zA-Z0-9]/g, '')?.slice(0, CODE_LENGTH);
    onChange(pastedData);

    const nextIndex = Math.min(pastedData?.length, CODE_LENGTH - 1);
    inputRefs?.current?.[nextIndex]?.focus();
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-8 gap-1 sm:gap-2 mb-2">
        {Array.from({ length: CODE_LENGTH })?.map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="text"
            maxLength={1}
            value={code?.[index]?.trim() || ''}
            onChange={(e) => handleChange(index, e?.target?.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={disabled}
            // ใช้ font 16px (text-base) ตั้งแต่แรก เพื่อกัน iOS Safari บังคับ override
            // + h-14/16 ให้มี breathing room พอไม่ให้ W/M ถูก clip
            // + min-w-0 + px-0 กันตัวอักษรถูกบีบที่จอแคบ
            className={`w-full min-w-0 px-0 h-14 sm:h-14 md:h-16 text-center text-base font-semibold leading-none rounded-lg sm:rounded-xl border transition-all duration-300 align-middle ${
              error
                ? 'border-error bg-error/5 text-error focus:border-error focus:shadow-[0_0_20px_rgba(220,47,2,0.3)]'
                : 'border-[#333] bg-[#111] text-foreground focus:border-[#fe8338] focus:shadow-[0_0_20px_rgba(254,131,56,0.2),0_0_40px_rgba(249,110,34,0.1)]'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'} outline-none`}
            aria-label={`Verification code character ${index + 1}`}
          />
        ))}
      </div>
      {error && (
        <p className="text-sm text-error text-center mt-2" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default VerificationCodeInput;
